import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import { API_PREFIX } from "../config";
import usePagedList from "../hooks/usePagedList";
import { downloadApiFile } from "../utils/download";
import { statusBadgeClass, statusLabel } from "../utils/statusBadge";

const EVENTS_PATH = `${API_PREFIX}/audit/security-events`;

/** Query string of the active filters (shared by the list and the CSV export). */
export function securityFilterQuery({ event, actorId }) {
  const params = new URLSearchParams();
  if (event) params.set("event", event);
  if (actorId) params.set("actor_id", actorId);
  const query = params.toString();
  return query ? `?${query}` : "";
}

const compactDetail = (detail) =>
  (detail && Object.keys(detail).length ? JSON.stringify(detail) : "");

/** Admin security-event log (sign-ins, SSO, role / ACL / grant changes):
 * filter by event and actor, cursor-paginated ("load more"), CSV export of the
 * current filters. Page size comes from the backend default. */
export default function SecurityEventsLog() {
  const { t } = useTranslation();
  const [event, setEvent] = useState("");
  const [actorId, setActorId] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const [actors, setActors] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [sideError, setSideError] = useState("");

  const query = securityFilterQuery({ event, actorId });
  const { items, loading, error, hasMore, loadMore } = usePagedList(`${EVENTS_PATH}${query}`);

  // Filter options: the event vocabulary and the accounts that can be actors.
  // Either failing only leaves its filter with "All" — the log itself still loads.
  useEffect(() => {
    let stale = false;
    apiFetchJson(`${EVENTS_PATH}/types`)
      .then((data) => { if (!stale) setEventTypes((data && data.events) || []); })
      .catch((err) => { if (!stale) setSideError(err.message); });
    apiFetchJson(`${API_PREFIX}/auth/users`)
      .then((users) => { if (!stale) setActors(Array.isArray(users) ? users : []); })
      .catch((err) => { if (!stale) setSideError(err.message); });
    return () => { stale = true; };
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    setSideError("");
    try {
      await downloadApiFile(`${EVENTS_PATH}/export${query}`, "audit_security_events.csv");
    } catch (err) {
      setSideError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const message = error || sideError;

  return (
    <>
      <p className="ds-page-subtitle">{t("audit.securitySubtitle")}</p>
      <div className="ds-toolbar">
        <div className="ds-field">
          <label className="ds-label" htmlFor="security-event">{t("audit.event")}</label>
          <select id="security-event" className="ds-select" value={event} onChange={(e) => setEvent(e.target.value)}>
            <option value="">{t("audit.allEvents")}</option>
            {eventTypes.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
        </div>
        <div className="ds-field">
          <label className="ds-label" htmlFor="security-actor">{t("audit.actor")}</label>
          <select id="security-actor" className="ds-select" value={actorId} onChange={(e) => setActorId(e.target.value)}>
            <option value="">{t("audit.allActors")}</option>
            {actors.map((u) => <option key={u.id} value={u.id}>{u.email}</option>)}
          </select>
        </div>
        <div className="audit-export">
          <button type="button" className="ds-btn ds-btn--secondary" disabled={exporting} onClick={exportCsv}>
            {exporting ? t("audit.exporting") : t("audit.exportCsv")}
          </button>
        </div>
      </div>

      {message && <div className="ds-alert ds-alert--danger" role="alert">{message}</div>}
      {loading && items.length === 0 ? (
        <div className="ds-empty">{t("audit.loading")}</div>
      ) : items.length === 0 ? (
        <div className="ds-empty">{t("audit.empty")}</div>
      ) : (
        <div className="ds-table-wrap">
          <table className="ds-table audit-table">
            <thead>
              <tr>
                <th>{t("audit.time")}</th><th>{t("audit.event")}</th><th>{t("audit.outcome")}</th>
                <th>{t("audit.actor")}</th><th>{t("audit.target")}</th><th>{t("audit.ip")}</th>
                <th>{t("audit.requestId")}</th><th>{t("audit.detail")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const detail = compactDetail(it.detail);
                return (
                  <tr key={it.id}>
                    <td className="audit-time">{new Date(it.created_at).toLocaleString()}</td>
                    <td className="ds-mono">{it.event}</td>
                    <td><span className={statusBadgeClass(it.outcome)}>{statusLabel(it.outcome)}</span></td>
                    <td>{it.actor_email || it.actor_id || ""}</td>
                    <td>{[it.target_type, it.target_id].filter(Boolean).join(": ")}</td>
                    <td className="ds-mono">{it.ip || ""}</td>
                    <td className="ds-mono audit-request-id">{it.request_id || ""}</td>
                    <td className="audit-detail">
                      {detail && <code title={JSON.stringify(it.detail, null, 2)}>{detail}</code>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {hasMore && (
        <button type="button" className="ds-btn ds-btn--secondary audit-load-more" onClick={loadMore}
                disabled={loading}>
          {t("audit.loadMore")}
        </button>
      )}
    </>
  );
}
